import { useRef, useState } from "react";
import CameraIcon from "../../../public/images/icons/camera.svg";
import AvatarEditor from 'react-avatar-editor';
import cls from 'clsx';
import { TextButton } from "../../Buttons/TextButton";
import IncrementIcon from "../../../public/images/icons/increment.svg";
import DecrementIcon from "../../../public/images/icons/decrement.svg";
import { UnderlinedButton } from "../../Buttons/UnderlinedButton";
import { IconContainer } from "../../Buttons/IconContainer";
import UploadIcon from "../../../public/images/icons/upload.svg";
import { NextButton } from "../../Create/Navigation/NextButton";
import { setAvatar } from "@jconradi/tutu-porn-api-client/src/me";
import { ErrorMessage } from "../../Form/Inputs/ErrorMessage";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";

export const AvatarChooser = ({ avatar }: any) => {
    const inputRef = useRef<any>();
    const ref = useRef<any>(null);
    const [image, setImage] = useState<any>();
    const [scale, setScale] = useState(1.2);
    const [isSaving, setSaving] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [error, setError] = useState<string>();
    const { t } = useTranslation('settings');

    const onInputChange = (event: any) => {
        try {
            if (event.target.files && event.target.files[0]) {
                const file = event.target.files[0];
    
                if (file.size > 1000 * 1000 * 10) {
                    setError(t('editProfile.avatar.errorTooBig'));
                    return;
                }
                setError(undefined);
                const image = URL.createObjectURL(event.target.files[0])
                setImage(image);
                // setFile(event.target.files[0]);
            }
            else {
                setImage(undefined);
                // setFile(undefined);
            }
        }
        finally {
            inputRef.current.value = null;
        }
    }

    const reset = () => {
        setScale(1.2);
        setRotation(0);
        setError(undefined);
        setImage(undefined);
    }

    const save = async () => {
        if (isSaving || !ref.current) {
            return;
        }

        setError(undefined);
        setSaving(true);

        try {
            const canvas = ref.current.getImage();
            const avatarBlob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, ));

            const formData = new FormData();
            formData.append("avatar", avatarBlob);

            await setAvatar(formData);

            reset();
        }
        catch (err) {
            console.error(err);
            setError(t('editProfile.avatar.uploadFailed'))
        }
        finally {
            setSaving(false);
        }
    }

    const chooseImage = () => {
        if (!image) {
            inputRef?.current.click();
        }
    }

    const chooseAnother = () => {
        setImage(undefined);
    }

    const incScale = () => {
        setScale(scale => scale + .1);
    }

    const decScale = () => {
        setScale(scale => Math.max(1, scale - .1));
    }

    return (
        <div className={cls("w-full rounded-2xl gap-x-4 flex flex-col lg:flex-row items-center px-4 py-8", {
            "bg-base-100": !!image
        })}>
        <div onClick={chooseImage} className="max-w-[250px] w-full aspect-square rounded-2xl bg-secondary relative overflow-hidden">
            <div className="h-full w-full cursor-pointer
             flex flex-col items-center justify-center text-center gap-y-2">
                {image &&
                <AvatarEditor
                ref={ref}
                className="w-full h-full absolute inset-0"
                image={image}
                borderRadius={24}
                    width={240}
                    height={240}
                    color={[127, 127, 127, 0.6]} // RGBA
                    scale={scale}
                    rotate={rotation}
                />
                }
                {!image &&
                    <>
                        {avatar &&
                            <Image unoptimized priority width={240} height={240} src={avatar} alt="User Avatar" className="absolute inset-0 w-full h-full" /> 
                        }
                        <CameraIcon className="w-8 h-8 text-white" />
                        {t('editProfile.avatar.instructions')}
                    </>
                }
                <input disabled={!!image} ref={inputRef} onChange={onInputChange} type="file" accept="image/*" className="hidden"></input>
            </div>
        </div>
        <div className="w-full flex-1 self-start flex flex-col gap-y-4">
            {image &&
            <>
                <p className="font-semibold text-center lg:text-left">{t('editProfile.avatar.title')}</p>
                <div className="">
                <TextButton onClick={decScale} className="after:content-none bg-base-300" side="left">
                    <DecrementIcon className="w-8 h-8" />
                </TextButton>
                <TextButton onClick={incScale} className="after:content-none bg-base-300"  side="right">
                    <IncrementIcon className="w-8 h-8" />
                </TextButton>
                </div>
                <input type="range" min="0" max="360" value={rotation} onChange={(e) => setRotation(+e.target.value)} className="range range-primary" />

                <div className="flex flex-row">
                    <UnderlinedButton onClick={chooseAnother}>
                        <IconContainer icon={<UploadIcon className="h-8 w-8 text-white stroke-[3px]" />} label={t('editProfile.avatar.uploadAnother')} />
                    </UnderlinedButton>
                    <NextButton loading={isSaving} label={t('editProfile.avatar.continue')} onNext={save} />
                </div>

            </>
            }
            {error && <ErrorMessage className="font-semibold text-lg text-center lg:text-left" message={error}>{error}</ErrorMessage>}

        </div>
        </div>
    )
}